import { Component, computed, inject } from '@angular/core';
import { DialogRef } from '@angular/cdk/dialog';
import { NgTemplateOutlet } from '@angular/common';
import { RRol } from '@cumlaude/service-contract';
import { IconComponent, IconName } from '@cumlaude/shared-components-icons';
import { ToggleButtonComponent } from '@cumlaude/shared-components-buttons';
import { UserService } from '../../../services/user.service';
import { UrlService } from '../../../services/url.service';
import { NavigatieVoorkeurService } from '../../../services/navigatie-voorkeur.service';
import { PasfotoComponent } from '../../../shared/components/pasfoto/pasfoto.component';

/**
 * ProfielMenuComponent voert de acties zelf uit, pas nadat de popover gesloten is.
 */
export type ProfielMenuActie = 'switch' | 'releasenotes' | 'wachtwoord' | 'beheer' | 'terug' | 'uitloggen' | 'navigatie-feedback';

interface ProfielMenuItem {
	actie: ProfielMenuActie;
	label: string;
	icon: IconName;
}

@Component({
	selector: 'app-profiel-menu-dialog',
	templateUrl: './profiel-menu-dialog.component.html',
	styleUrls: ['./profiel-menu-dialog.component.scss'],
	imports: [IconComponent, PasfotoComponent, ToggleButtonComponent, NgTemplateOutlet],
})
export class ProfielMenuDialogComponent {
	dialogRef = inject<DialogRef<ProfielMenuActie>>(DialogRef);
	protected readonly userService = inject(UserService);
	protected readonly urlService = inject(UrlService);
	protected readonly navigatieVoorkeurService = inject(NavigatieVoorkeurService);

	protected readonly schoolnaam = computed(() => this.userService.account()?.instelling.naam ?? '');
	protected readonly pasfotoKey = computed(() => this.userService.account()?.afkorting);
	protected readonly kanSwitchen = computed(() => this.userService.account()?.multiAccount ?? false);
	protected readonly kanWachtwoordWijzigen = computed(() => !!this.userService.account()?.credentialsSub);
	protected readonly isBeheerder = computed(() => this.userService.rollen()?.includes(RRol.BEHEERDER) ?? false);

	protected readonly items = computed<ProfielMenuItem[]>(() => [
		{ actie: 'releasenotes', label: 'Releasenotes', icon: 'releasenotes' },
		...(this.kanWachtwoordWijzigen() ? [<ProfielMenuItem>{ actie: 'wachtwoord', label: 'Wachtwoord wijzigen', icon: 'edit' }] : []),
		...(this.isBeheerder() ? [this.beheerItem()] : []),
		{ actie: 'uitloggen', label: 'Uitloggen', icon: 'uitloggen' },
	]);

	private beheerItem(): ProfielMenuItem {
		return this.urlService.isBeheer()
			? { actie: 'terug', label: 'Terug naar CumLaude', icon: 'settings' }
			: { actie: 'beheer', label: 'Beheer', icon: 'settings' };
	}

	kies(actie: ProfielMenuActie) {
		this.dialogRef.close(actie);
	}

	onNavigatieToggle(waarde: boolean) {
		const vraagReden = !waarde && this.navigatieVoorkeurService.moetFeedbackVragen();
		this.navigatieVoorkeurService.setNieuweNavigatie(waarde);
		if (vraagReden) this.kies('navigatie-feedback');
	}
}
